import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { navigate } from 'gatsby';
import { Alert, Button, Icon, List, Modal } from '@pella/sash-framework';
import { LoadingOverlay } from '../../../';
import { currencyFormat } from '../../../utilities/functions';
import LoginForm from '../../../page/account/common/login-form';
import styles from './index.module.scss';
import { useOktaAuth } from '../../../../hooks/use-okta-auth';
import { useCart } from '../../../../hooks/use-cart';
import { useMachine } from '@xstate/react';
import { cartMachine } from '../../../../machines/shop/cart';
import { fromPromise } from 'xstate';
import AlertList from '../../../page/shop/common/alert-list';
import { useSelector } from 'react-redux';
import { useCartHeader } from '../../../../hooks/use-cart-header';

const CartList = ({ className }) => {
	const cartListRef = useRef(null);
	const [errors, setErrors] = useState([]);
	const [modalOpen, setModalOpen] = useState(false);
	const { deleteCartItem } = useCart();
	const { isLogged, profile } = useOktaAuth();
	const locations = useSelector((state) => state.locations);
	const { cart } = useCartHeader();

	const [snapshot, sendToCartMachine] = useMachine(
		cartMachine.provide({
			actors: {
				deleteCartItem: fromPromise(({ input }) => deleteCartItem(input.item.sku)),
			},
		}),
		{
			input: {
				cart: cart,
				branchRegion: locations?.service_info?.branch_region,
				postalCode: locations?.market?.postal_code,
				placement: 'cart-list',
				isPro: profile?.isPro,
			},
		}
	);

	const { context, value: cartStatus } = snapshot;
	const { cartErrors, hasPriceChange } = context;
	const { Items: cartItems, total_number: cartItemQuantity, priceEventReason, meta } = cart || {};

	useEffect(() => {
		const onClickOutside = (e) => {
			if (e.target && cartListRef?.current && !cartListRef.current.contains(e.target)) {
				const styles = cartListRef.current.getAttribute('style')?.split(';');
				if (styles?.includes('visibility: visible')) {
					cartListRef.current.setAttribute('style', null);
				}
			}
		};

		document.addEventListener('click', onClickOutside, false);
		return () => document.removeEventListener('click', onClickOutside, false);
	}, []);

	const setCartItem = (sku) => {
		localStorage.setItem('editingItem', JSON.stringify({ src: 'cart', quoteID: cart.quote_id, itemID: sku }));
	};

	const signIn = () => setModalOpen(true);
	const onAuthCloseModal = () => navigate('/shop/checkout/');
	const closeModal = () => setModalOpen(false);

	useEffect(() => {
		const { deleteCartItem: deleteCartItemError, getCart: getCartError } = cartErrors || {};

		const formatError = (key, error, errorMessage) => {
			return error ? { [key]: errorMessage } : null;
		};

		const cartError = formatError('cart', getCartError, getCartError);
		const deleteItemError = formatError('deleteItem', deleteCartItemError, deleteCartItemError);

		const newErrors = [deleteItemError, cartError].filter(Boolean);
		setErrors(newErrors);
	}, [cartErrors]);

	return (
		<div
			className={`header-cartList ${styles.cartList} ${className || ''}`}
			id='cartList'
			ref={cartListRef}
		>
			{cartStatus !== 'idle' && (
				<LoadingOverlay
					fixed={false}
					svgSize={200}
				/>
			)}

			{hasPriceChange && priceEventReason === 'region' && (
				<Alert
					type='info'
					showCollapse
					title='Messages about items in your cart'
					className='mx-5 my-4'
				>
					<p className='text-body-2 mb-3'>The items below have changed price due to Job Site address change.</p>
					<List>
						{cartItems?.map((item, index) => {
							const { unit_price_change, brand_name, product_name, unit_price } = item;
							unit_price_change?.type !== 'equal' && (
								<li key={index}>
									<p className='text-body-2'>{`${brand_name ? brand_name + ' - ' + product_name : product_name} - has ${unit_price_change.type}d from ${currencyFormat(unit_price.amount - unit_price_change.amount)} to ${unit_price.formatted}`}</p>
								</li>
							);
						})}
					</List>
				</Alert>
			)}

			<AlertList
				errors={errors}
				className='mb-4'
			/>

			<div className={styles.cartList_Scroller}>
				{cartItems?.map((item, index) => {
					const { brand_name, product_name, quantity, value, unit_price, item_url, product_image_url, sku } = item;

					return (
						<div
							key={`${index}-${sku}`}
							className={styles.cartList_Item}
						>
							<div className={styles.cartList_Image}>
								<a
									href={item_url}
									onClick={() => setCartItem(sku)}
								>
									<img
										src={product_image_url}
										alt={`${brand_name} - ${product_name}}`}
										loading='lazy'
									/>
								</a>
							</div>
							<div className={styles.cartList_ProductInfo}>
								<p className='pr-7 font-semibold'>
									{`(${quantity}) `}
									<a
										href={item_url}
										onClick={() => setCartItem(sku)}
									>
										{brand_name ? `${brand_name} - ${product_name}` : product_name}
									</a>
								</p>

								<button
									className={styles.cartList_Remove}
									onClick={() => sendToCartMachine({ type: 'DELETE_ITEM', item })}
									aria-label='Remove Item'
								>
									<span className={styles.srOnly}>Remove Item</span>
									<Icon name='delete' />
								</button>
								<p className='mt-1 font-semibold'>{value.formatted}</p>
								{quantity > 1 && <p className='m-0 text-xs'>{`(${unit_price.formatted} each)`}</p>}
							</div>
						</div>
					);
				})}
			</div>

			<p className={styles.cartList_CartSubtotal}>
				<span>Subtotal:</span>
				<span>{meta?.display_price?.subtotal?.formatted}</span>
			</p>

			<div className={styles.cartList_Buttons}>
				<Button
					color='primary'
					href={isLogged ? `/shop/checkout/` : null}
					onClick={!isLogged ? () => signIn() : null}
					size='sm'
					wide
				>
					Checkout
				</Button>
				<Button
					color='secondary'
					href='/shop/cart/'
					size='sm'
					wide
				>
					View Cart ({cartItemQuantity})
				</Button>
			</div>

			<Modal
				effect='slidedown'
				isOpen={modalOpen}
				onClose={closeModal}
				className={styles.cartSignInModal}
			>
				<Modal.Header headingLevel={1}>&nbsp;</Modal.Header>
				<Modal.Content>
					<LoginForm
						type='isModal'
						requestedUrl='/shop/checkout/'
						onAuthCloseModal={onAuthCloseModal}
					/>
				</Modal.Content>
			</Modal>
		</div>
	);
};

CartList.propTypes = {
	className: PropTypes.string,
};

export default CartList;
